import { useEffect, useRef, useState } from "react";
import { useCurrency } from "@/hooks/useCurrency";
import { cn } from "@/lib/utils";
import { TextArea } from "@/components/ui/textarea.tsx";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/custom/button.tsx";
import { IoClose, IoCheckmark } from "react-icons/io5";
import { useCssObserver } from "@/hooks/use-css-observer";

import { showActionErrorToast } from "@/state/error-toast";

interface InlineInputProps {
  value?: string | number;
  onSave?: (newValue: string | undefined) => Promise<any>;
  disabled?: boolean;
  disableResize?: boolean;
  sendOnEnter?: boolean;
  className?: string;
  type?: "text" | "number";
  showCurrency?: boolean;
  fit?: boolean;
  onChange?: (value: string) => void;
  buttonLocation?: "top" | "bottom" | "left" | "right";
}

const InlineInput: React.FC<InlineInputProps> = ({
  value,
  onSave,
  disabled = false,
  disableResize = true,
  sendOnEnter = true,
  className = "",
  type = "text",
  showCurrency = false,
  fit,
  onChange,
  buttonLocation = "bottom",
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  // const [height, setHeight] = useState(0);
  // const [width, setWidth] = useState(0);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const divRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { currencySymbol } = useCurrency();

  const handleSave = async () => {
    try {
      if (onSave) {
        await onSave(tempValue?.toString());
      }
      if (onChange) {
        onChange(tempValue?.toString() || "");
      }
      setIsEditing(false);
    } catch (error: any) {
      // Show error toast and keep editing mode open
      showActionErrorToast(error, "Validation failed");
    }
  };

  const handleCancel = () => {
    setTempValue(value);
    setIsEditing(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey && sendOnEnter) {
      // if (event.shiftKey || !sendOnEnter) {
      //   if (textAreaRef.current) {
      //     textAreaRef.current.style.height = "auto";
      //     textAreaRef.current.rows += 1;
      //   }
      // } else {
      event.preventDefault();
      handleSave();
      // }
    }
  };

  // add 'lose focus' behavior to exit edit mode
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsEditing(false);
      }
    };

    if (isEditing) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditing]);

  const mirrorRef = useRef<HTMLDivElement | null>(null);

  useCssObserver({
    target: mirrorRef,
    storage: wrapperRef,
    mapper: { "--input-mirror-width": "width" },
  });

  useCssObserver({
    target: divRef,
    storage: wrapperRef,
    mapper: { "--input-div-height": "height", "--input-div-width": "width" },
  });

  useEffect(() => {
    if (!isEditing && value !== undefined) {
      setTempValue(value);
    }
  }, [value]);

  const rowsRef = useRef(0);

  const buttonClasses = cn(
    "absolute z-40 flex space-x-2",
    buttonLocation === "bottom" && "-bottom-10 right-0",
    buttonLocation === "top" && "-top-10 right-0",
    buttonLocation === "left" && "top-0 left-10",
    buttonLocation === "right" && "top-0 right-10",
  );

  const length =
    value && type === "number" && showCurrency && value.toString().length;

  return (
    <div
      className={cn(
        "relative transition-all min-w-36 w-full max-w-full",
        fit ? "w-fit" : "w-full",
        !(disabled || isEditing) && "hover:cursor-auto hover:bg-primary/10",
        className,
      )}
      onClick={() => !isEditing && !disabled && setIsEditing(true)}
      ref={wrapperRef}
    >
      {isEditing ? (
        type === "text" ? (
          <>
            <TextArea
              className={cn(
                "p-0 whitespace-pre-wrap min-h-4 h-[calc(var(--input-div-height))] max-w-full w-[calc(max(var(--input-mirror-width)+30px,9rem))]",
                fit
                  ? "max-w-full w-[calc(max(var(--input-mirror-width)+30px,9rem))]"
                  : "w-full",
                disableResize && "resize-none",
              )}
              value={tempValue}
              onChange={(e) => {
                onChange?.((e.target as HTMLTextAreaElement).value);
                try {
                  const rows = (e.target as HTMLTextAreaElement).value?.split(
                    "\n",
                  )?.length;
                  (e.target as HTMLTextAreaElement).rows = (
                    e.target as HTMLTextAreaElement
                  ).value?.split("\n")?.length;
                  const prevRows = rowsRef.current;
                  rowsRef.current = (e.target as HTMLTextAreaElement).rows;
                  if (prevRows != rows && rows > 1) {
                    (e.target as HTMLTextAreaElement).style.height = "auto";
                  }
                } catch (ex) {
                  console.error(ex);
                }
                setTempValue((e.target as HTMLTextAreaElement).value);
              }}
              onKeyDown={handleKeyDown}
              ref={(el) => {
                el?.focus();
                textAreaRef.current = el;
              }}
            />
            <div
              ref={mirrorRef}
              className="absolute left-0 top-0 min-w-36 invisible h-[0px] w-min whitespace-pre-line text-nowrap inline-block"
            >
              {tempValue}
            </div>
          </>
        ) : (
          // Type === "number"
          <div className="flex items-center">
            {showCurrency && <span className="mr-2">{currencySymbol}</span>}
            <Input
              className={cn(
                "p-0 h-6 whitespace-pre-wrap",
                disableResize && "resize-none",
              )}
              type="number"
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              ref={(el) => {
                el?.focus();
              }}
            />
          </div>
        )
      ) : (
        <div
          className={cn(
            "p-0 whitespace-pre-wrap w-auto",
            !value && "min-h-[18px]",
          )}
          ref={divRef}
        >
          {value &&
            type === "number" &&
            showCurrency &&
            typeof length === "number" && <span>{currencySymbol}</span>}
          {value}
        </div>
      )}
      {isEditing && (
        <div className={buttonClasses}>
          <Button variant="opaque" className="border" onClick={handleSave}>
            <IoCheckmark />
          </Button>
          <Button variant="opaque" className="border" onClick={handleCancel}>
            <IoClose />
          </Button>
        </div>
      )}
    </div>
  );
};

export default InlineInput;
